import { useEffect } from "react";
import { useAuth } from "@context/shared/AuthContext";
import { useThemeMode } from "@context/shared/ThemeContext";
import useUserPreferencesStore from "@stores/shared/useUserPreferencesStore";
import { isAccreditor } from "@config/shared/roles";
import i18n from "@locales/i18n";

// Renderless component that hydrates ThemeContext's mode/isDyslexic, and
// applies the user's language, from the user-preferences API (keys
// "color-mode" / "enable-dyslexic-font" / "preferred-language") once
// isAuthenticated is true. Lives inside AuthProvider (see App.jsx) because
// ThemeProvider wraps AuthProvider and has no auth state of its own, so it
// can't safely gate this fetch itself. No localStorage/sessionStorage here.
export const ThemePreferencesLoader = () => {
  const { isAuthenticated, user } = useAuth();
  const { hydrateMode, hydrateIsDyslexic } = useThemeMode();
  const { getPreference } = useUserPreferencesStore();

  useEffect(() => {
    if (!isAuthenticated) return;

    const loadThemePreferences = async () => {
      try {
        const colorMode = await getPreference("color-mode", "light");
        hydrateMode(colorMode);
      } catch (error) {
        console.error("Failed to load color-mode preference:", error);
      }

      // Accreditors have no dyslexic-font toggle in their stripped-down UI,
      // and the preference endpoint 403s for that role — skip it entirely.
      if (!isAccreditor(user?.roleId)) {
        try {
          const dyslexicFont = await getPreference(
            "enable-dyslexic-font",
            false,
          );
          hydrateIsDyslexic(dyslexicFont === true || dyslexicFont === "true");
        } catch (error) {
          console.error(
            "Failed to load enable-dyslexic-font preference:",
            error,
          );
        }
      }

      try {
        const preferredLanguage = await getPreference(
          "preferred-language",
          "nl",
        );
        if (preferredLanguage && preferredLanguage !== i18n.language) {
          await i18n.changeLanguage(preferredLanguage);
        }
      } catch (error) {
        console.error("Failed to load preferred-language preference:", error);
      }
    };

    loadThemePreferences();
  }, [isAuthenticated, user, getPreference, hydrateMode, hydrateIsDyslexic]);

  return null;
};
